// src/pages/QuestionnaireEditorPage.js
//
// Ticket 06 full tree editor: in-browser CRUD over the questionnaire
// tree at questionnaires/{activeStudyId}. Admins can add/remove/reorder
// sections and leaf questions, rename them, change their type, and
// edit options for mcq / multi_select_mcq. Group questions are rendered
// as inert blocks — they round-trip through save but their sub_questions
// aren't editable here (still reachable via JSON import/export, which
// stays alongside the tree editor).
//
// Edits happen on a local draft copy; nothing is written until the
// admin clicks Save. Save re-runs the same validate() as JSON import
// so the two paths share a single source of truth about well-formedness.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  Upload,
  Download,
  AlertTriangle,
  CheckCircle,
  Loader,
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  Save,
  RotateCcw,
} from 'lucide-react';
import {
  loadQuestionnaire,
  saveQuestionnaire,
  getAllLeafQuestions,
} from '../data/questionnaireHelpers';
import { useActiveStudy } from '../hooks/useActiveStudy';

const LEAF_TYPES = ['mcq', 'multi_select_mcq', 'long_answer'];

function normalizeUploadedJson(raw, { orgId, studyId }) {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Uploaded file is not a JSON object.');
  }
  if (Array.isArray(raw.questionnaire)) {
    return {
      orgId,
      studyId,
      version: 1,
      sections: raw.questionnaire.map((s, i) => ({
        sectionId: s.section_id || `s${i + 1}`,
        title: s.section_title,
        description: s.section_description,
        questions: s.questions || [],
      })),
    };
  }
  if (Array.isArray(raw.sections)) {
    return {
      ...raw,
      orgId,
      studyId,
      version: raw.version || 1,
    };
  }
  throw new Error(
    'Uploaded JSON does not match the expected shape. Expected either { questionnaire: [...] } or { sections: [...] }.',
  );
}

function validate(q) {
  if (!q || !Array.isArray(q.sections) || q.sections.length === 0) {
    return 'Questionnaire must have at least one section.';
  }
  const ids = new Set();
  const walk = (nodes, path) => {
    for (const node of nodes || []) {
      if (!node.id && node.type !== 'group') {
        return `Question at ${path} is missing an id.`;
      }
      if (node.id) {
        if (ids.has(node.id)) {
          return `Duplicate question id "${node.id}". Every question must have a unique id across the whole questionnaire.`;
        }
        ids.add(node.id);
      }
      if (!node.type) {
        return `Question "${node.id || path}" is missing a type.`;
      }
      if (!['mcq', 'multi_select_mcq', 'long_answer', 'group'].includes(node.type)) {
        return `Question "${node.id || path}" has unknown type "${node.type}".`;
      }
      if ((node.type === 'mcq' || node.type === 'multi_select_mcq') && !Array.isArray(node.options)) {
        return `Question "${node.id}" of type ${node.type} must have an options array.`;
      }
      if (node.type === 'group' && !Array.isArray(node.sub_questions)) {
        return `Group question "${node.id}" must have a sub_questions array.`;
      }
      if (node.type === 'group') {
        const err = walk(node.sub_questions, `${path}>${node.id}`);
        if (err) return err;
      }
    }
    return null;
  };
  for (let i = 0; i < q.sections.length; i += 1) {
    const s = q.sections[i];
    if (!s.title) return `Section at index ${i} is missing a title.`;
    if (!Array.isArray(s.questions)) return `Section "${s.title}" is missing a questions array.`;
    const err = walk(s.questions, `section[${i}]`);
    if (err) return err;
  }
  return null;
}

function blankLeafQuestion(existingIds) {
  let n = 1;
  while (existingIds.has(`q_${n}`)) n += 1;
  return {
    id: `q_${n}`,
    type: 'long_answer',
    text: 'New question',
  };
}

function blankSection(existingSectionIds) {
  let n = 1;
  while (existingSectionIds.has(`s${n}`)) n += 1;
  return {
    sectionId: `s${n}`,
    title: 'New section',
    description: '',
    questions: [],
  };
}

function collectAllIds(draft) {
  const ids = new Set();
  const walk = (nodes) => {
    for (const n of nodes || []) {
      if (n.id) ids.add(n.id);
      if (n.type === 'group') walk(n.sub_questions);
    }
  };
  (draft?.sections || []).forEach((s) => walk(s.questions));
  return ids;
}

function cloneDraft(q) {
  // Deep clone via JSON — the tree is all plain data (no Firestore
  // Timestamps here since loadQuestionnaire returns the stored doc
  // unchanged and ids/titles/options are all primitives).
  return JSON.parse(JSON.stringify(q || { sections: [] }));
}

const QuestionnaireEditorPage = () => {
  const { orgId, studyId, studyName, loading: studyLoading } = useActiveStudy();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ message: '', type: 'idle' });
  const fileRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const q = await loadQuestionnaire(studyId);
        if (cancelled) return;
        setQuestionnaire(q);
        setDraft(cloneDraft(q || { orgId, studyId, version: 0, sections: [] }));
      } catch (err) {
        if (!cancelled) {
          setStatus({ message: `Failed to load questionnaire: ${err.message}`, type: 'error' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [studyId, orgId]);

  const dirty = useMemo(() => {
    if (!questionnaire && !draft) return false;
    return JSON.stringify(questionnaire || { sections: [] }) !== JSON.stringify(draft || { sections: [] });
  }, [questionnaire, draft]);

  const updateDraft = (fn) => {
    setDraft((prev) => {
      const next = cloneDraft(prev);
      fn(next);
      return next;
    });
  };

  // --- Section ops ---
  const addSection = () => {
    updateDraft((d) => {
      const existing = new Set((d.sections || []).map((s) => s.sectionId));
      d.sections = [...(d.sections || []), blankSection(existing)];
    });
  };
  const deleteSection = (idx) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete this section and all its questions?`)) return;
    updateDraft((d) => {
      d.sections.splice(idx, 1);
    });
  };
  const moveSection = (idx, dir) => {
    updateDraft((d) => {
      const target = idx + dir;
      if (target < 0 || target >= d.sections.length) return;
      const [s] = d.sections.splice(idx, 1);
      d.sections.splice(target, 0, s);
    });
  };
  const patchSection = (idx, patch) => {
    updateDraft((d) => {
      d.sections[idx] = { ...d.sections[idx], ...patch };
    });
  };

  // --- Question ops ---
  const addQuestion = (sectionIdx) => {
    updateDraft((d) => {
      const allIds = collectAllIds(d);
      d.sections[sectionIdx].questions.push(blankLeafQuestion(allIds));
    });
  };
  const deleteQuestion = (sectionIdx, qIdx) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this question?')) return;
    updateDraft((d) => {
      d.sections[sectionIdx].questions.splice(qIdx, 1);
    });
  };
  const moveQuestion = (sectionIdx, qIdx, dir) => {
    updateDraft((d) => {
      const list = d.sections[sectionIdx].questions;
      const target = qIdx + dir;
      if (target < 0 || target >= list.length) return;
      const [q] = list.splice(qIdx, 1);
      list.splice(target, 0, q);
    });
  };
  const patchQuestion = (sectionIdx, qIdx, patch) => {
    updateDraft((d) => {
      const q = d.sections[sectionIdx].questions[qIdx];
      const next = { ...q, ...patch };
      // Type transitions: when switching into an options-bearing type,
      // seed an empty options array if the previous type didn't have one.
      if ((next.type === 'mcq' || next.type === 'multi_select_mcq') && !Array.isArray(next.options)) {
        next.options = [];
      }
      // When switching away from options-bearing types, drop the options
      // field so validate() doesn't flag it.
      if (next.type !== 'mcq' && next.type !== 'multi_select_mcq') {
        delete next.options;
      }
      d.sections[sectionIdx].questions[qIdx] = next;
    });
  };

  const handleSave = async () => {
    if (!draft) return;
    const err = validate(draft);
    if (err) {
      setStatus({ message: `Cannot save: ${err}`, type: 'error' });
      return;
    }
    setSaving(true);
    setStatus({ message: 'Saving...', type: 'loading' });
    try {
      await saveQuestionnaire(studyId, { ...draft, orgId, studyId }, { updatedBy: 'admin-editor' });
      const fresh = await loadQuestionnaire(studyId);
      setQuestionnaire(fresh);
      setDraft(cloneDraft(fresh));
      setStatus({ message: 'Questionnaire saved.', type: 'success' });
    } catch (e) {
      setStatus({ message: `Save failed: ${e.message}`, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Discard unsaved changes?')) return;
    setDraft(cloneDraft(questionnaire || { orgId, studyId, version: 0, sections: [] }));
    setStatus({ message: 'Changes discarded.', type: 'info' });
  };

  const handleExport = () => {
    if (!questionnaire) {
      setStatus({ message: 'Nothing to export — the questionnaire has not been loaded yet.', type: 'error' });
      return;
    }
    const blob = new Blob([JSON.stringify(questionnaire, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `questionnaire-${studyId}-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setStatus({ message: 'Download started.', type: 'success' });
  };

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      setStatus({ message: 'Only .json files are accepted.', type: 'error' });
      return;
    }
    try {
      const text = await file.text();
      const raw = JSON.parse(text);
      const normalized = normalizeUploadedJson(raw, { orgId, studyId });
      const err = validate(normalized);
      if (err) {
        setStatus({ message: `Import rejected: ${err}`, type: 'error' });
        e.target.value = null;
        return;
      }
      const confirmMsg = `This will REPLACE the current questionnaire for study "${studyId}" with the uploaded file (${getAllLeafQuestions(normalized).length} leaf questions in ${normalized.sections.length} sections). Continue?`;
      // eslint-disable-next-line no-alert
      if (!window.confirm(confirmMsg)) {
        e.target.value = null;
        return;
      }
      setStatus({ message: 'Saving...', type: 'loading' });
      await saveQuestionnaire(studyId, normalized, { updatedBy: 'admin-import' });
      const fresh = await loadQuestionnaire(studyId);
      setQuestionnaire(fresh);
      setDraft(cloneDraft(fresh));
      setStatus({ message: 'Questionnaire imported successfully.', type: 'success' });
    } catch (err) {
      setStatus({ message: `Import failed: ${err.message}`, type: 'error' });
    } finally {
      e.target.value = null;
    }
  };

  const sectionCount = draft ? (draft.sections || []).length : 0;
  const leafCount = draft ? getAllLeafQuestions(draft).length : 0;

  const statusStyles = {
    idle: 'bg-slate-100 text-slate-700',
    info: 'bg-blue-100 text-blue-800',
    success: 'bg-green-100 text-green-800',
    error: 'bg-red-100 text-red-800',
    loading: 'bg-yellow-100 text-yellow-800',
  };

  return (
    <main className="p-4 lg:p-8 pb-32">
      <div className="mb-6">
        <Link
          to="/admin/dashboard"
          className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-800"
        >
          <ChevronLeft size={18} />
          Back to Progress Overview
        </Link>
      </div>
      <div className="bg-white p-6 rounded-xl border border-slate-200 academic-shadow-lg max-w-4xl">
        <div className="flex items-start justify-between mb-4 gap-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-800 mb-1">Questionnaire editor</h2>
            <p className="text-sm text-slate-600">
              Editing questionnaire for study <strong>{studyName || studyId}</strong>
              {studyName && studyId ? (
                <span className="text-slate-500"> ({studyId})</span>
              ) : null}
              . {sectionCount} section
              {sectionCount === 1 ? '' : 's'}, {leafCount} leaf question{leafCount === 1 ? '' : 's'}.
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={handleDiscard}
              disabled={!dirty || saving}
              className="inline-flex items-center gap-2 px-3 py-2 bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <RotateCcw size={14} />
              Discard
            </button>
            <button
              onClick={handleSave}
              disabled={!dirty || saving}
              className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
              Save
            </button>
          </div>
        </div>

        {status.message && (
          <div className={`p-3 rounded-lg mb-4 text-sm flex items-center gap-3 ${statusStyles[status.type]}`}>
            {status.type === 'error' && <AlertTriangle size={16} />}
            {status.type === 'success' && <CheckCircle size={16} />}
            {status.type === 'loading' && <Loader size={16} className="animate-spin" />}
            <span>{status.message}</span>
          </div>
        )}

        {(loading || studyLoading) ? (
          <div className="py-8 text-slate-500 flex items-center gap-3">
            <Loader size={20} className="animate-spin" /> Loading current questionnaire...
          </div>
        ) : (
          <div className="space-y-4">
            {(draft?.sections || []).map((section, sIdx) => (
              <SectionEditor
                key={section.sectionId || sIdx}
                section={section}
                sIdx={sIdx}
                isFirst={sIdx === 0}
                isLast={sIdx === (draft.sections.length - 1)}
                patchSection={patchSection}
                deleteSection={deleteSection}
                moveSection={moveSection}
                addQuestion={addQuestion}
                patchQuestion={patchQuestion}
                deleteQuestion={deleteQuestion}
                moveQuestion={moveQuestion}
              />
            ))}
            <button
              onClick={addSection}
              className="w-full p-3 border-2 border-dashed border-slate-300 text-slate-600 rounded-lg hover:bg-slate-50 text-sm font-semibold flex items-center justify-center gap-2"
            >
              <Plus size={16} /> Add section
            </button>
          </div>
        )}

        <div className="mt-10 border-t pt-6">
          <h3 className="text-sm font-bold text-slate-700 mb-2">JSON round-trip (advanced)</h3>
          <p className="text-xs text-slate-500 mb-3">
            Export the current saved questionnaire, or replace it wholesale from a JSON file.
            Import overwrites any unsaved edits above.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => fileRef.current && fileRef.current.click()}
              className="inline-flex items-center gap-2 px-3 py-2 bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 text-sm font-semibold"
            >
              <Upload size={14} /> Import JSON
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              onChange={handleImportFile}
              className="hidden"
            />
            <button
              onClick={handleExport}
              disabled={!questionnaire}
              className="inline-flex items-center gap-2 px-3 py-2 bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download size={14} /> Export JSON
            </button>
          </div>
        </div>
      </div>
    </main>
  );
};

const SectionEditor = ({
  section,
  sIdx,
  isFirst,
  isLast,
  patchSection,
  deleteSection,
  moveSection,
  addQuestion,
  patchQuestion,
  deleteQuestion,
  moveQuestion,
}) => (
  <div
    className="border border-slate-200 rounded-lg p-4 bg-slate-50"
    data-section-idx={sIdx}
  >
    <div className="flex items-start gap-2 mb-3">
      <div className="flex-1">
        <input
          type="text"
          value={section.title || ''}
          onChange={(e) => patchSection(sIdx, { title: e.target.value })}
          placeholder="Section title"
          className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-base font-semibold text-slate-800 focus-academic"
          aria-label={`Section ${sIdx + 1} title`}
        />
        <input
          type="text"
          value={section.description || ''}
          onChange={(e) => patchSection(sIdx, { description: e.target.value })}
          placeholder="Optional description"
          className="w-full mt-2 px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-600 focus-academic"
        />
      </div>
      <div className="flex flex-col gap-1">
        <button
          onClick={() => moveSection(sIdx, -1)}
          disabled={isFirst}
          className="p-1.5 bg-white border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Move section up"
        >
          <ArrowUp size={14} />
        </button>
        <button
          onClick={() => moveSection(sIdx, 1)}
          disabled={isLast}
          className="p-1.5 bg-white border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Move section down"
        >
          <ArrowDown size={14} />
        </button>
        <button
          onClick={() => deleteSection(sIdx)}
          className="p-1.5 bg-white border border-red-200 text-red-600 rounded hover:bg-red-50"
          aria-label="Delete section"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>

    <div className="space-y-2 pl-4 border-l-2 border-slate-200">
      {(section.questions || []).map((q, qIdx) => (
        <QuestionEditor
          key={q.id || qIdx}
          question={q}
          sIdx={sIdx}
          qIdx={qIdx}
          isFirst={qIdx === 0}
          isLast={qIdx === (section.questions.length - 1)}
          patchQuestion={patchQuestion}
          deleteQuestion={deleteQuestion}
          moveQuestion={moveQuestion}
        />
      ))}
      <button
        onClick={() => addQuestion(sIdx)}
        className="w-full p-2 border border-dashed border-slate-300 text-slate-600 rounded text-xs font-semibold hover:bg-slate-100 flex items-center justify-center gap-2"
      >
        <Plus size={14} /> Add question
      </button>
    </div>
  </div>
);

const QuestionEditor = ({
  question,
  sIdx,
  qIdx,
  isFirst,
  isLast,
  patchQuestion,
  deleteQuestion,
  moveQuestion,
}) => {
  if (question.type === 'group') {
    return (
      <div className="bg-white border border-slate-200 rounded p-3 text-xs">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-slate-800">
              {question.title || question.id} <span className="text-slate-400">(group)</span>
            </div>
            <div className="text-slate-500">
              {(question.sub_questions || []).length} sub-questions — edit via JSON round-trip
            </div>
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => moveQuestion(sIdx, qIdx, -1)}
              disabled={isFirst}
              className="p-1 bg-white border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-30"
              aria-label="Move group up"
            >
              <ArrowUp size={12} />
            </button>
            <button
              onClick={() => moveQuestion(sIdx, qIdx, 1)}
              disabled={isLast}
              className="p-1 bg-white border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-30"
              aria-label="Move group down"
            >
              <ArrowDown size={12} />
            </button>
            <button
              onClick={() => deleteQuestion(sIdx, qIdx)}
              className="p-1 bg-white border border-red-200 text-red-600 rounded hover:bg-red-50"
              aria-label="Delete group"
            >
              <Trash2 size={12} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isOptionsType = question.type === 'mcq' || question.type === 'multi_select_mcq';

  return (
    <div className="bg-white border border-slate-200 rounded p-3" data-question-idx={qIdx}>
      <div className="flex items-start gap-2 mb-2">
        <div className="flex-1 grid grid-cols-12 gap-2">
          <input
            type="text"
            value={question.id || ''}
            onChange={(e) => patchQuestion(sIdx, qIdx, { id: e.target.value })}
            placeholder="question id"
            className="col-span-4 px-2 py-1.5 bg-slate-50 border border-slate-300 rounded text-xs font-mono text-slate-700 focus-academic"
            aria-label={`Question ${qIdx + 1} id`}
          />
          <select
            value={question.type || 'long_answer'}
            onChange={(e) => patchQuestion(sIdx, qIdx, { type: e.target.value })}
            className="col-span-8 px-2 py-1.5 bg-slate-50 border border-slate-300 rounded text-xs font-medium text-slate-700 focus-academic"
            aria-label={`Question ${qIdx + 1} type`}
          >
            {LEAF_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          <button
            onClick={() => moveQuestion(sIdx, qIdx, -1)}
            disabled={isFirst}
            className="p-1 bg-white border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-30"
            aria-label="Move question up"
          >
            <ArrowUp size={12} />
          </button>
          <button
            onClick={() => moveQuestion(sIdx, qIdx, 1)}
            disabled={isLast}
            className="p-1 bg-white border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-30"
            aria-label="Move question down"
          >
            <ArrowDown size={12} />
          </button>
          <button
            onClick={() => deleteQuestion(sIdx, qIdx)}
            className="p-1 bg-white border border-red-200 text-red-600 rounded hover:bg-red-50"
            aria-label="Delete question"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>
      <textarea
        value={question.text || ''}
        onChange={(e) => patchQuestion(sIdx, qIdx, { text: e.target.value })}
        placeholder="Question text shown to the reviewer"
        rows={2}
        className="w-full px-2 py-1.5 bg-slate-50 border border-slate-300 rounded text-sm text-slate-700 focus-academic resize-y"
      />
      {isOptionsType && (
        <input
          type="text"
          value={(question.options || []).join('|')}
          onChange={(e) =>
            patchQuestion(sIdx, qIdx, {
              options: e.target.value
                .split('|')
                .map((o) => o.trim())
                .filter(Boolean),
            })
          }
          placeholder="Options separated by | (e.g. low | mid | high)"
          className="w-full mt-2 px-2 py-1.5 bg-slate-50 border border-slate-300 rounded text-xs text-slate-700 focus-academic"
          aria-label={`Question ${qIdx + 1} options`}
        />
      )}
    </div>
  );
};

export default QuestionnaireEditorPage;
